import { Link } from "react-router-dom";
import { QrCode, UtensilsCrossed, LayoutDashboard, ArrowRight, Image, ChefHat } from "lucide-react";

const features = [
  { icon: QrCode, title: "QR per table", desc: "Generate a printable QR code for every table. Guests scan and order in seconds." },
  { icon: UtensilsCrossed, title: "Menu management", desc: "Add dishes, prices, photos and dietary tags. Toggle availability instantly." },
  { icon: Image, title: "Your brand", desc: "Upload your logo and cover, pick an accent colour — make the menu yours." },
  { icon: LayoutDashboard, title: "Live kitchen board", desc: "Watch orders roll in and move them from pending to ready in real time." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#121110] text-[#F5F3EF] overflow-hidden">
      <header className="flex items-center justify-between px-6 md:px-12 py-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-orange-600 grid place-items-center">
            <ChefHat className="h-5 w-5 text-white" />
          </div>
          <span className="font-sub font-bold text-xl tracking-tight">QRFoody</span>
        </div>
        <div className="flex items-center gap-3">
          <Link data-testid="nav-login-link" to="/login" className="text-sm font-medium text-[#A39E96] hover:text-white transition-colors">
            Sign in
          </Link>
          <Link data-testid="nav-register-link" to="/register" className="text-sm font-semibold bg-orange-600 hover:bg-orange-700 transition-colors px-4 py-2 rounded-full">
            Start free
          </Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <span className="font-mono-j text-xs uppercase tracking-widest text-orange-500">Multi-restaurant · QR ordering</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mt-4">
            Turn every table into a <span className="text-orange-500 italic">self-serve</span> checkout.
          </h1>
          <p className="text-[#A39E96] text-base sm:text-lg mt-6 max-w-lg leading-relaxed">
            A complete QR ordering platform for restaurants. Your guests scan, browse your branded menu and order — no app, no waiting. You run the kitchen from one live board.
          </p>
          <div className="flex flex-wrap gap-4 mt-8">
            <Link data-testid="hero-get-started-btn" to="/register" className="group inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 transition-all px-6 py-3.5 rounded-full font-semibold shadow-lg shadow-orange-900/40 hover:scale-[1.02]">
              Create your restaurant <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link data-testid="hero-signin-btn" to="/login" className="inline-flex items-center gap-2 border border-[#3a3733] hover:border-orange-600 transition-colors px-6 py-3.5 rounded-full font-semibold">
              I have an account
            </Link>
          </div>
        </div>
        <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
          <img
            src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Restaurant table"
            className="rounded-3xl shadow-2xl object-cover w-full h-[460px] border border-white/10"
          />
          <div className="absolute -bottom-6 -left-6 bg-white text-[#1F1D1B] rounded-2xl p-4 shadow-2xl hidden sm:block">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-[#121110] grid place-items-center">
                <QrCode className="h-7 w-7 text-orange-500" />
              </div>
              <div>
                <p className="font-sub font-bold text-sm">Table 4 just ordered</p>
                <p className="text-xs text-gray-500">2× Truffle Burger · $37.00</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div key={f.title} className="bg-[#1C1A18] border border-white/5 rounded-2xl p-6 hover:border-orange-600/40 transition-colors fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="h-11 w-11 rounded-xl bg-orange-600/15 grid place-items-center mb-4">
                <f.icon className="h-5 w-5 text-orange-500" />
              </div>
              <h3 className="font-sub font-semibold text-lg">{f.title}</h3>
              <p className="text-sm text-[#A39E96] mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
